import { infiniteQueryOptions, queryOptions, skipToken } from '@tanstack/react-query';
import uniq from 'lodash/uniq';

import { ScheduleV2ListQuery, ScheduleV2ListResponse } from 'chaika-frontend-types/frontend-cms';

import { ScheduleV2Api } from '../api/ScheduleV2Api';
import { ScheduleV2UrlState } from '../routes/scheduleV2Route';

export const SCHEDULE_V2_PAGE_SIZE = 100;

export type ScheduleV2FilterQuery = Omit<ScheduleV2ListQuery, 'limit' | 'offset'>;

export interface DeviceScheduleFilter {
    organizationId: string | undefined;
    deviceId: string[];
    from: string;
    to: string;
    cancelledReservations: boolean;
}

export interface UncoveredScheduleFilter {
    organizationId: string | undefined;
    userId: string[];
    from: string;
    to: string;
}

// confidential and notWorking are client-only: they must not reach the request or invalidate the query key as is
export function normalizeScheduleV2UrlState({
    confidential,
    notWorking,
    ...urlState
}: ScheduleV2UrlState): Omit<ScheduleV2UrlState, 'confidential' | 'notWorking'> {
    return {
        ...urlState,
        userId: normalizeIds(urlState.userId),
        officeId: normalizeIds(urlState.officeId),
        specialtyId: normalizeIds(urlState.specialtyId),
        serviceId: normalizeIds(urlState.serviceId),
    };
}

export function buildScheduleV2Query(urlState: ScheduleV2UrlState): ScheduleV2FilterQuery {
    const normalized = normalizeScheduleV2UrlState(urlState);

    return {
        from: normalized.from,
        to: normalized.to,
        orderBy: normalized.orderBy,
        organizationId: normalized.organizationId,
        userId: joinIds(normalized.userId),
        officeId: joinIds(normalized.officeId),
        specialtyId: joinIds(normalized.specialtyId),
        serviceId: joinIds(normalized.serviceId),
        patientId: normalized.patientId,
        cancelledReservations: normalized.cancelledReservations || undefined,
        withMovedReservationGhosts: normalized.cancelledReservations || undefined,
        offHours: requestsOffHours(urlState) || undefined,
    };
}

export const scheduleV2Keys = {
    all: ['scheduleV2'] as const,
    infiniteLists: () => [...scheduleV2Keys.all, 'infiniteList'] as const,
    infiniteList: (urlState: ScheduleV2UrlState) =>
        [
            ...scheduleV2Keys.infiniteLists(),
            { ...normalizeScheduleV2UrlState(urlState), offHours: requestsOffHours(urlState) },
        ] as const,
    dayLists: () => [...scheduleV2Keys.all, 'dayList'] as const,
    dayList: (userId: string | undefined, day: string) => [...scheduleV2Keys.dayLists(), { userId, day }] as const,
    uncoveredLists: () => [...scheduleV2Keys.all, 'uncoveredList'] as const,
    uncoveredList: (filter: UncoveredScheduleFilter) =>
        [...scheduleV2Keys.uncoveredLists(), { ...filter, userId: normalizeIds(filter.userId) }] as const,
    deviceLists: () => [...scheduleV2Keys.all, 'deviceList'] as const,
    deviceList: (filter: DeviceScheduleFilter) =>
        [...scheduleV2Keys.deviceLists(), { ...filter, deviceId: normalizeIds(filter.deviceId) }] as const,
};

export function getNextScheduleV2PageOffset(
    lastPage: ScheduleV2ListResponse,
    allPages: ScheduleV2ListResponse[],
): number | undefined {
    const loaded = allPages.reduce((total, page) => total + page.schedules.length, 0);

    if (lastPage.schedules.length === 0 || loaded >= lastPage.count) {
        return undefined;
    }

    return loaded;
}

export const scheduleV2QueryOptions = {
    infiniteList: (urlState: ScheduleV2UrlState) =>
        infiniteQueryOptions({
            staleTime: Infinity,
            queryKey: scheduleV2Keys.infiniteList(urlState),
            queryFn: ({ pageParam }) =>
                ScheduleV2Api.getList({
                    ...buildScheduleV2Query(urlState),
                    limit: SCHEDULE_V2_PAGE_SIZE,
                    offset: pageParam,
                }),
            initialPageParam: 0,
            getNextPageParam: getNextScheduleV2PageOffset,
            gcTime: 0, // Always fetch new data
        }),
    dayList: (userId: string | undefined, day: string) =>
        queryOptions({
            queryKey: scheduleV2Keys.dayList(userId, day),
            queryFn: userId ? () => ScheduleV2Api.getList({ userId, from: day, to: day, offHours: true }) : skipToken,
        }),
    uncoveredList: (filter: UncoveredScheduleFilter) =>
        queryOptions({
            queryKey: scheduleV2Keys.uncoveredList(filter),
            queryFn: () =>
                ScheduleV2Api.getUncoveredList({
                    organizationId: filter.organizationId,
                    userId: joinIds(normalizeIds(filter.userId)),
                    from: filter.from,
                    to: filter.to,
                }),
        }),
    deviceList: (filter: DeviceScheduleFilter) =>
        queryOptions({
            queryKey: scheduleV2Keys.deviceList(filter),
            queryFn: filter.organizationId
                ? () =>
                      ScheduleV2Api.getDeviceList({
                          organizationId: filter.organizationId,
                          deviceId: joinIds(normalizeIds(filter.deviceId)),
                          from: filter.from,
                          to: filter.to,
                          cancelledReservations: filter.cancelledReservations || undefined,
                      })
                : skipToken,
        }),
};

function requestsOffHours({ notWorking, officeId }: ScheduleV2UrlState): boolean {
    return notWorking && officeId.length > 0;
}

function joinIds(ids: string[]): string | undefined {
    return ids.length > 0 ? ids.join(',') : undefined;
}

function normalizeIds(ids: string[]): string[] {
    return uniq(ids).sort();
}
